// AutismAi Games Module - 24 playable educational games
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Trophy, Star, Heart, Play, RotateCcw, ArrowRight, Check, X, Sparkles, Award, Lock, GripVertical } from "lucide-react";
import { DndContext, useDraggable, useDroppable, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors, DragOverlay, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useT } from "./i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (iso, lang = "ar") => { try { return new Date(iso).toLocaleString(lang === "en" ? "en-US" : "ar", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; } };

const LEVEL_LABEL = {
  1: { ar: "مبتدئ", en: "Beginner" },
  2: { ar: "متوسط", en: "Intermediate" },
  3: { ar: "متقدم", en: "Advanced" },
};
const LEVEL_COLOR = { 1: "bg-emerald-100 text-emerald-700", 2: "bg-amber-100 text-amber-700", 3: "bg-rose-100 text-rose-700" };
const CAT_LABEL = {
  colors:    { ar: "الألوان",   en: "Colors" },
  shapes:    { ar: "الأشكال",   en: "Shapes" },
  numbers:   { ar: "الأرقام",   en: "Numbers" },
  letters:   { ar: "الحروف",    en: "Letters" },
  routines:  { ar: "الروتين",   en: "Routines" },
  matching:  { ar: "المطابقة",  en: "Matching" },
  cognitive: { ar: "معرفي",     en: "Cognitive" },
};
const lvl = (n, lang) => (LEVEL_LABEL[n] && LEVEL_LABEL[n][lang]) || (LEVEL_LABEL[n] && LEVEL_LABEL[n].ar) || n;
const cat = (k, lang) => (CAT_LABEL[k] && CAT_LABEL[k][lang]) || (CAT_LABEL[k] && CAT_LABEL[k].ar) || k;
// pick localized field e.g. obj.title_ar / obj.title_en
const loc = (obj, prefix, lang) => obj?.[`${prefix}_${lang}`] || obj?.[`${prefix}_ar`] || obj?.[prefix] || "";

const Btn = ({ children, variant = "primary", ...p }) => {
  const s = { primary:"bg-[#2F5233] hover:bg-[#1E3622] text-white", outline:"bg-white hover:bg-[#F0EFEB] text-[#1A201E] border border-[#E5E2DB]", ghost:"hover:bg-[#F0EFEB] text-[#4A5553]" };
  return <button {...p} className={`px-4 py-2 rounded-lg font-medium transition disabled:opacity-50 ${s[variant]} ${p.className||""}`}>{children}</button>;
};
const Card = ({ children, className="" }) => <div className={`bg-white rounded-xl border border-[#E5E2DB] p-5 shadow-sm ${className}`}>{children}</div>;

// ============ Visual renderer ============
function Visual({ data, size = "lg" }) {
  if (!data) return null;
  const wrap = size === "xl" ? "text-7xl" : size === "lg" ? "text-5xl" : "text-3xl";
  if (data.type === "color") return <div className="w-full h-32 rounded-xl shadow-inner" style={{ background: data.hex }}/>;
  if (data.type === "emoji" || data.type === "text") return <div className={`text-center ${wrap} font-bold leading-none`}>{data.value}</div>;
  if (data.type === "dots") return <div className="flex flex-wrap gap-2 justify-center max-w-xs mx-auto">{Array.from({length:data.count}).map((_,i)=><div key={i} className="w-6 h-6 rounded-full bg-[#2F5233]"/>)}</div>;
  if (data.type === "emoji_count") return <div className="flex flex-wrap gap-1 justify-center text-3xl">{Array.from({length:data.count}).map((_,i)=><span key={i}>{data.emoji}</span>)}</div>;
  return null;
}

function OptionVisual({ visual }) {
  if (!visual) return null;
  if (visual.type === "color") return <div className="w-16 h-16 mx-auto rounded-lg" style={{ background: visual.hex }}/>;
  if (visual.type === "emoji_count") return <div className="flex flex-wrap gap-0.5 justify-center text-xl">{Array.from({length:visual.count}).map((_,i)=><span key={i}>{visual.emoji}</span>)}</div>;
  return null;
}

// ============ Engines ============
function MultipleChoiceEngine({ game, onComplete }) {
  const { lang } = useT();
  const [idx, setIdx] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [picked, setPicked] = useState(null);
  const round = game.rounds[idx];
  const total = game.rounds.length;

  const pick = (i) => {
    if (picked !== null) return;
    setPicked(i);
    const isRight = round.options[i].correct;
    if (isRight) setCorrect(c => c + 1);
    setTimeout(() => {
      if (idx + 1 < total) { setIdx(idx + 1); setPicked(null); }
      else onComplete({ correct: correct + (isRight ? 1 : 0), total });
    }, 1200);
  };

  return (
    <div className="space-y-5">
      <div className="text-sm text-[#4A5553] text-center">{lang === "ar" ? `السؤال ${idx+1} من ${total}` : `Question ${idx+1} of ${total}`}</div>
      <div className="bg-[#F0EFEB] h-2 rounded-full"><div className="bg-[#2F5233] h-2 rounded-full transition-all" style={{width:`${((idx)/total)*100}%`}}/></div>
      <h2 className="text-2xl font-bold text-center font-heading">{loc(round, "question", lang)}</h2>
      {round.visual && <Card className="bg-[#F9F8F6] py-8"><Visual data={round.visual} size="xl"/></Card>}
      <div className="grid grid-cols-2 gap-3">
        {round.options.map((o, i) => {
          let cls = "bg-white hover:bg-[#F0EFEB] border-[#E5E2DB]";
          if (picked !== null) {
            if (i === picked && o.correct) cls = "bg-[#3A7D44]/15 border-[#3A7D44]";
            else if (i === picked && !o.correct) cls = "bg-[#D9534F]/15 border-[#D9534F]";
            else if (o.correct) cls = "bg-[#3A7D44]/10 border-[#3A7D44]";
          }
          return <button key={i} data-testid={`opt-${i}`} onClick={()=>pick(i)} disabled={picked!==null}
            className={`p-5 rounded-xl border-2 transition text-lg font-medium ${cls}`}>
              {o.visual ? <OptionVisual visual={o.visual}/> : <span>{loc(o, "label", lang)}</span>}
              {picked === i && o.correct && <Check className="inline ms-2 text-[#3A7D44]" size={18}/>}
              {picked === i && !o.correct && <X className="inline ms-2 text-[#D9534F]" size={18}/>}
            </button>;
        })}
      </div>
    </div>
  );
}

function MatchingEngine({ game, onComplete }) {
  const { lang } = useT();
  const [left] = useState(() => game.pairs.map(p => ({ id: p.id, side: "a", content: p.a })).sort(() => Math.random()-0.5));
  const [right] = useState(() => game.pairs.map(p => ({ id: p.id, side: "b", content: p.b })).sort(() => Math.random()-0.5));
  const [selected, setSelected] = useState(null);
  const [matched, setMatched] = useState([]);
  const [wrongs, setWrongs] = useState(0);
  const total = game.pairs.length;

  const renderContent = (c) => c.type === "emoji" ? <span className="text-3xl">{c.value}</span> :
    <span className="text-base font-medium">{(lang === "en" ? (c.en || c.ar) : (c.ar || c.en)) || c.value}</span>;

  const click = (side, id) => {
    if (matched.includes(id)) return;
    if (!selected) { setSelected({ side, id }); return; }
    if (selected.side === side) { setSelected({ side, id }); return; }
    if (selected.id === id) {
      const newMatched = [...matched, id];
      setMatched(newMatched); setSelected(null);
      if (newMatched.length === total) {
        const correct = total - wrongs > 0 ? total : Math.max(1, total - wrongs);
        onComplete({ correct: Math.max(0, total - wrongs), total });
      }
    } else {
      setWrongs(w => w + 1); setSelected(null);
    }
  };

  return (
    <div className="space-y-5">
      <h2 className="text-2xl font-bold text-center font-heading">{loc(game, "title", lang)}</h2>
      <p className="text-center text-sm text-[#4A5553]">{lang === "ar" ? "طابق العناصر" : "Match the items"} · {matched.length}/{total}</p>
      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-2">{left.map(item => {
          const isSel = selected?.side==="a" && selected.id===item.id;
          const isMatched = matched.includes(item.id);
          return <button key={`a-${item.id}`} data-testid={`match-a-${item.id}`} onClick={()=>click("a", item.id)} disabled={isMatched}
            className={`w-full p-4 rounded-xl border-2 transition ${isMatched?"bg-[#3A7D44]/15 border-[#3A7D44] opacity-70":isSel?"bg-[#769FCD]/15 border-[#769FCD]":"bg-white border-[#E5E2DB] hover:bg-[#F0EFEB]"}`}>
            {renderContent(item.content)}</button>;
        })}</div>
        <div className="space-y-2">{right.map(item => {
          const isSel = selected?.side==="b" && selected.id===item.id;
          const isMatched = matched.includes(item.id);
          return <button key={`b-${item.id}`} data-testid={`match-b-${item.id}`} onClick={()=>click("b", item.id)} disabled={isMatched}
            className={`w-full p-4 rounded-xl border-2 transition ${isMatched?"bg-[#3A7D44]/15 border-[#3A7D44] opacity-70":isSel?"bg-[#769FCD]/15 border-[#769FCD]":"bg-white border-[#E5E2DB] hover:bg-[#F0EFEB]"}`}>
            {renderContent(item.content)}</button>;
        })}</div>
      </div>
    </div>
  );
}

function SequencingEngine({ game, onComplete }) {
  const { lang } = useT();
  const [pool, setPool] = useState(() => [...game.sequence].sort(()=>Math.random()-0.5));
  const [placed, setPlaced] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const total = game.sequence.length;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const reset = () => { setPool([...game.sequence].sort(()=>Math.random()-0.5)); setPlaced([]); };
  const submit = () => {
    let correct = 0;
    placed.forEach((p, i) => { if (p.order === i + 1) correct++; });
    onComplete({ correct, total });
  };

  const findItem = (id) => pool.find(p=>p.id===id) || placed.find(p=>p.id===id);

  const onDragEnd = (e) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over) return;
    const item = findItem(active.id);
    if (!item) return;
    // dropping into placed zone
    if (over.id === "placed-zone" || placed.find(p=>p.id===over.id)) {
      if (pool.find(p=>p.id===active.id)) {
        setPool(p => p.filter(x => x.id !== active.id));
        // determine insertion index
        if (over.id === "placed-zone" || over.id === active.id) {
          setPlaced(s => [...s, item]);
        } else {
          const idx = placed.findIndex(p=>p.id===over.id);
          setPlaced(s => [...s.slice(0, idx), item, ...s.slice(idx)]);
        }
      } else {
        // reordering inside placed
        const oldIdx = placed.findIndex(p=>p.id===active.id);
        const newIdx = placed.findIndex(p=>p.id===over.id);
        if (oldIdx !== -1 && newIdx !== -1) setPlaced(arr => arrayMove(arr, oldIdx, newIdx));
      }
    }
    // dropping back to pool
    if (over.id === "pool-zone") {
      if (placed.find(p=>p.id===active.id)) {
        setPlaced(s => s.filter(x => x.id !== active.id));
        setPool(p => [...p, item]);
      }
    }
  };

  const active = activeId ? findItem(activeId) : null;

  return (
    <div className="space-y-5">
      <h2 className="text-2xl font-bold text-center font-heading">{loc(game, "title", lang)}</h2>
      <p className="text-center text-sm text-[#4A5553]">{lang === "ar" ? "اسحب أو انقر العنصر، ثم ضعه بترتيبه الصحيح" : "Drag or tap an item, then drop it in the correct order"}</p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={e=>setActiveId(e.active.id)} onDragEnd={onDragEnd}>
        <PlacedZone items={placed}/>
        <PoolZone items={pool} onClickItem={(item)=>{
          setPool(p=>p.filter(x=>x.id!==item.id));
          setPlaced(s=>[...s,item]);
        }}/>
        <DragOverlay>{active ? <SeqChip item={active} dragging/> : null}</DragOverlay>
      </DndContext>
      <div className="flex gap-2 justify-center">
        <Btn variant="outline" onClick={reset}><RotateCcw size={16} className="inline ms-1"/>{lang === "ar" ? "إعادة" : "Reset"}</Btn>
        <Btn data-testid="seq-submit" onClick={submit} disabled={placed.length !== total}>{lang === "ar" ? "تأكيد الترتيب" : "Confirm order"}</Btn>
      </div>
    </div>
  );
}

function SeqChip({ item, dragging }) {
  const { lang } = useT();
  return <div className={`flex items-center gap-2 bg-white border-2 px-3 py-2 rounded-lg ${dragging?"border-[#769FCD] shadow-lg scale-105 cursor-grabbing":"border-[#E5E2DB] hover:border-[#769FCD] cursor-grab"}`}>
    <GripVertical size={14} className="text-[#4A5553]"/>
    <span className="text-2xl">{item.visual}</span>
    <span className="text-sm font-medium">{loc(item, "label", lang)}</span>
  </div>;
}

function SortableSeqItem({ item, index }) {
  const { lang } = useT();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging?0.3:1 };
  return <div ref={setNodeRef} style={style} {...attributes} {...listeners} data-testid={`placed-${item.id}`} className="touch-none">
    <div className="flex items-center gap-2 bg-white border-2 border-[#2F5233] px-3 py-2 rounded-lg cursor-grab active:cursor-grabbing">
      <span className="bg-[#2F5233] text-white w-6 h-6 rounded-full text-xs flex items-center justify-center">{index+1}</span>
      <span className="text-2xl">{item.visual}</span><span className="text-sm font-medium">{loc(item, "label", lang)}</span>
    </div>
  </div>;
}

function PlacedZone({ items }) {
  const { lang } = useT();
  const { setNodeRef, isOver } = useDroppable({ id: "placed-zone" });
  return <Card ref={setNodeRef} className={`bg-[#F9F8F6] transition ${isOver?"ring-2 ring-[#2F5233]":""}`}>
    <p className="text-sm font-medium mb-2">{lang === "ar" ? "العناصر المرتبة:" : "Ordered items:"}</p>
    <SortableContext items={items.map(i=>i.id)} strategy={verticalListSortingStrategy}>
      <div className="flex flex-wrap gap-2 min-h-[80px] items-center" data-testid="placed-zone">
        {items.length===0 ? <span className="text-sm text-[#4A5553]">{lang === "ar" ? "اسحب العناصر من الأسفل أو انقرها" : "Drag items from below or tap them"}</span> :
          items.map((p, i) => <SortableSeqItem key={p.id} item={p} index={i}/>)}
      </div>
    </SortableContext>
  </Card>;
}

function DraggableSeqItem({ item, onClick }) {
  const { lang } = useT();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: item.id });
  const style = { transform: CSS.Translate.toString(transform), opacity: isDragging?0.3:1 };
  return <div ref={setNodeRef} style={style} {...attributes} {...listeners} data-testid={`seq-${item.id}`} className="touch-none">
    <div onClick={onClick} className="flex items-center gap-2 bg-white border-2 border-[#E5E2DB] hover:border-[#769FCD] px-4 py-3 rounded-lg transition cursor-grab active:cursor-grabbing">
      <GripVertical size={14} className="text-[#4A5553]"/>
      <span className="text-2xl">{item.visual}</span><span>{loc(item, "label", lang)}</span>
    </div>
  </div>;
}

function PoolZone({ items, onClickItem }) {
  const { lang } = useT();
  const { setNodeRef, isOver } = useDroppable({ id: "pool-zone" });
  return <div ref={setNodeRef} className={`p-2 rounded-lg transition ${isOver?"bg-[#F0EFEB] ring-2 ring-[#4A5553]/20":""}`}>
    <p className="text-sm font-medium mb-2">{lang === "ar" ? "العناصر المتاحة (اسحب أو انقر):" : "Available items (drag or tap):"}</p>
    <div className="flex flex-wrap gap-2 min-h-[60px]">{items.length===0 ? <span className="text-sm text-[#4A5553] italic">{lang === "ar" ? "كل العناصر مرتبة" : "All items placed"}</span> :
      items.map(item => <DraggableSeqItem key={item.id} item={item} onClick={()=>onClickItem(item)}/>)}</div>
  </div>;
}

function MemoryEngine({ game, onComplete }) {
  const { lang } = useT();
  const [cards, setCards] = useState(() => {
    const dup = game.cards.flatMap(c => [{ uid: `${c.id}-1`, id: c.id, visual: c.visual }, { uid: `${c.id}-2`, id: c.id, visual: c.visual }]);
    return dup.sort(()=>Math.random()-0.5);
  });
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [tries, setTries] = useState(0);
  const total = game.cards.length;

  const click = (idx) => {
    if (flipped.includes(idx) || matched.includes(cards[idx].id) || flipped.length === 2) return;
    const newFlipped = [...flipped, idx];
    setFlipped(newFlipped);
    if (newFlipped.length === 2) {
      setTries(t => t + 1);
      const [a, b] = newFlipped;
      if (cards[a].id === cards[b].id) {
        setTimeout(() => {
          const newMatched = [...matched, cards[a].id];
          setMatched(newMatched); setFlipped([]);
          if (newMatched.length === total) {
            const score = Math.max(1, total - Math.max(0, tries - total));
            onComplete({ correct: score, total });
          }
        }, 700);
      } else { setTimeout(() => setFlipped([]), 1000); }
    }
  };

  return (
    <div className="space-y-5">
      <h2 className="text-2xl font-bold text-center font-heading">{loc(game, "title", lang)}</h2>
      <p className="text-center text-sm text-[#4A5553]">{lang === "ar" ? `طابق البطاقات · المتطابق: ${matched.length}/${total} · المحاولات: ${tries}` : `Match the cards · Matched: ${matched.length}/${total} · Attempts: ${tries}`}</p>
      <div className={`grid gap-3 ${total <= 4 ? "grid-cols-4" : "grid-cols-4 md:grid-cols-4"}`}>
        {cards.map((c, i) => {
          const isShown = flipped.includes(i) || matched.includes(c.id);
          return <button key={c.uid} data-testid={`mem-${i}`} onClick={()=>click(i)} disabled={matched.includes(c.id)}
            className={`aspect-square rounded-xl border-2 text-4xl flex items-center justify-center transition ${isShown?"bg-white border-[#2F5233]":"bg-[#2F5233] border-[#2F5233] hover:bg-[#1E3622]"}`}>
            {isShown ? c.visual : <Star className="text-white" size={28}/>}</button>;
        })}
      </div>
    </div>
  );
}

function SortingEngine({ game, onComplete }) {
  const { lang } = useT();
  const [items, setItems] = useState(() => [...game.items].sort(()=>Math.random()-0.5));
  const [placed, setPlaced] = useState({}); // {item_id: cat_id}
  const [activeId, setActiveId] = useState(null);
  const total = game.items.length;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const placeItem = (itemId, catId) => {
    setPlaced(p => ({ ...p, [itemId]: catId }));
    setItems(arr => arr.filter(x => x.id !== itemId));
  };

  const onDragEnd = (e) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over) return;
    const cat = game.categories.find(c => c.id === over.id);
    if (cat) placeItem(active.id, cat.id);
  };

  const submit = () => {
    let correct = 0;
    for (const item of game.items) {
      if (placed[item.id] === item.correct_category) correct++;
    }
    onComplete({ correct, total });
  };
  const reset = () => { setItems([...game.items].sort(()=>Math.random()-0.5)); setPlaced({}); };

  const active = activeId ? game.items.find(i=>i.id===activeId) : null;
  const gridCols = game.categories.length >= 3 ? "grid-cols-3" : "grid-cols-2";

  return (
    <div className="space-y-5">
      <h2 className="text-2xl font-bold text-center font-heading">{loc(game, "title", lang)}</h2>
      <p className="text-center text-sm text-[#4A5553]">{lang === "ar" ? "اسحب كل عنصر إلى التصنيف المناسب" : "Drag each item to the correct category"}</p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={e=>setActiveId(e.active.id)} onDragEnd={onDragEnd}>
        <div>
          <p className="text-sm font-medium mb-2">{lang === "ar" ? "العناصر:" : "Items:"}</p>
          <div className="flex flex-wrap gap-2 min-h-[80px]">
            {items.length === 0 ? <span className="text-sm text-[#4A5553]">{lang === "ar" ? "صنّف كل العناصر ثم اضغط تأكيد" : "Sort all items, then tap confirm"}</span> :
              items.map(item => <DragSortItem key={item.id} item={item}/>)}
          </div>
        </div>
        <div className={`grid gap-3 ${gridCols}`}>
          {game.categories.map(cat => {
            const placedItems = Object.entries(placed).filter(([,c])=>c===cat.id).map(([iid])=>game.items.find(x=>x.id===iid));
            return <DropCategory key={cat.id} cat={cat} placedItems={placedItems}/>;
          })}
        </div>
        <DragOverlay>{active ? <SortChip item={active} dragging/> : null}</DragOverlay>
      </DndContext>
      <div className="flex gap-2 justify-center">
        <Btn variant="outline" onClick={reset}><RotateCcw size={16} className="inline ms-1"/>{lang === "ar" ? "إعادة" : "Reset"}</Btn>
        <Btn data-testid="sort-submit" onClick={submit} disabled={items.length > 0}>{lang === "ar" ? "تأكيد التصنيف" : "Confirm sort"}</Btn>
      </div>
    </div>
  );
}

function SortChip({ item, dragging }) {
  const { lang } = useT();
  return <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 ${dragging?"bg-[#769FCD]/15 border-[#769FCD] shadow-lg scale-105 cursor-grabbing":"bg-white border-[#E5E2DB] hover:border-[#769FCD] cursor-grab"}`}>
    <GripVertical size={12} className="text-[#4A5553]"/>
    <span className="text-2xl">{item.visual}</span><span className="text-sm">{loc(item, "label", lang)}</span>
  </div>;
}

function DragSortItem({ item }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: item.id });
  const style = { transform: CSS.Translate.toString(transform), opacity: isDragging?0.3:1 };
  return <div ref={setNodeRef} style={style} {...attributes} {...listeners} data-testid={`sort-item-${item.id}`} className="touch-none">
    <SortChip item={item}/>
  </div>;
}

function DropCategory({ cat, placedItems }) {
  const { lang } = useT();
  const { setNodeRef, isOver } = useDroppable({ id: cat.id });
  return <div ref={setNodeRef} data-testid={`sort-drop-${cat.id}`}
    className={`border-2 border-dashed rounded-xl p-3 min-h-[140px] transition ${isOver?"border-[#2F5233] bg-[#2F5233]/5":"border-[#E5E2DB]"}`}>
    <p className="text-center font-semibold mb-2">{loc(cat, "label", lang)}</p>
    <div className="flex flex-wrap gap-2">{placedItems.map(p =>
      <div key={p.id} className="px-2 py-1 bg-[#3A7D44]/10 rounded-lg text-sm flex items-center gap-1">
        <span className="text-xl">{p.visual}</span><span className="text-xs">{loc(p, "label", lang)}</span>
      </div>)}</div>
  </div>;
}

const ENGINES = {
  multiple_choice: MultipleChoiceEngine,
  image_choice: MultipleChoiceEngine,
  matching: MatchingEngine,
  sequencing: SequencingEngine,
  memory: MemoryEngine,
  sorting: SortingEngine,
};

// ============ Game Player ============
export function GamePlayer() {
  const { code } = useParams();
  const nav = useNavigate();
  const { lang } = useT();
  const [game, setGame] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [result, setResult] = useState(null);
  const [childId, setChildId] = useState("");
  const [children, setChildren] = useState([]);
  const [started, setStarted] = useState(false);
  const startTime = useRef(Date.now());

  useEffect(() => {
    axios.get(`${API}/games/${code}`).then(r => setGame(r.data));
    axios.get(`${API}/children`).then(r => setChildren(r.data));
  }, [code]);

  const start = async () => {
    const { data } = await axios.post(`${API}/games/sessions/start`, { game_code: code, child_id: childId || null });
    setSessionId(data.session_id); setStarted(true); startTime.current = Date.now();
  };

  const finish = async ({ correct, total }) => {
    const score = Math.round((correct / total) * 100);
    const duration = Math.round((Date.now() - startTime.current) / 1000);
    const { data } = await axios.post(`${API}/games/sessions/complete`, {
      session_id: sessionId, score, correct_count: correct, total_rounds: total, duration_seconds: duration,
    });
    setResult({ score, correct, total, ...data });
  };

  const replay = async () => {
    setResult(null); setStarted(false); setSessionId(null);
  };

  if (!game) return <div className="p-6 text-[#4A5553]">{lang === "ar" ? "جاري التحميل..." : "Loading..."}</div>;

  if (result) return <div className="max-w-2xl mx-auto p-6 fade-up" data-testid="results-screen">
    <Card className="text-center">
      <div className="text-7xl mb-4">{result.score >= 70 ? "🎉" : result.score >= 50 ? "👏" : "💪"}</div>
      <h2 className="text-3xl font-bold font-heading mb-2">{lang === "ar" ? "أحسنت!" : "Well done!"}</h2>
      <div className="text-6xl font-bold text-[#2F5233] my-4">{result.score}%</div>
      <p className="text-[#4A5553]">{lang === "ar" ? `${result.correct} إجابة صحيحة من أصل ${result.total}` : `${result.correct} correct out of ${result.total}`}</p>
      <p className="text-sm mt-3 text-[#4A5553]">{result.score >= 70 ? (lang === "ar" ? "أداء ممتاز، استمر!" : "Excellent performance, keep going!") : (lang === "ar" ? "محاولة جيدة، حاول مرة أخرى!" : "Good try, give it another go!")}</p>

      {result.new_badges?.length > 0 && <div className="mt-5 p-4 bg-[#E8A317]/10 rounded-xl border-2 border-[#E8A317]/30">
        <p className="text-sm font-semibold mb-2 flex items-center gap-1 justify-center"><Sparkles className="text-[#E8A317]" size={18}/>{lang === "ar" ? "أوسمة جديدة!" : "New badges!"}</p>
        <div className="flex flex-wrap gap-2 justify-center">{result.new_badges.map(b =>
          <div key={b.code} className="bg-white px-3 py-2 rounded-lg flex items-center gap-2 border border-[#E5E2DB]" data-testid={`new-badge-${b.code}`}>
            <span className="text-2xl">{b.icon}</span><span className="text-sm font-medium">{loc(b, "label", lang)}</span></div>)}</div>
      </div>}

      <div className="flex flex-wrap gap-2 justify-center mt-6">
        <Btn data-testid="replay-btn" onClick={replay}><RotateCcw size={16} className="inline ms-1"/>{lang === "ar" ? "إعادة اللعب" : "Replay"}</Btn>
        <Btn variant="outline" onClick={()=>nav("/games")}>{lang === "ar" ? "عودة للمكتبة" : "Back to library"}</Btn>
        {result.suggested_next && <Btn variant="outline" data-testid="next-game-btn" onClick={()=>nav(`/games/play/${result.suggested_next.code}`)}>{lang === "ar" ? "اللعبة التالية:" : "Next game:"} {loc(result.suggested_next, "title", lang)}<ArrowRight size={16} className="inline ms-1 rtl-mirror"/></Btn>}
      </div>
    </Card>
  </div>;

  if (!started) return <div className="max-w-2xl mx-auto p-6 fade-up">
    <Card>
      <div className="text-center">
        <div className="text-6xl mb-3">{game.icon}</div>
        <h1 className="text-3xl font-bold font-heading mb-2">{loc(game, "title", lang)}</h1>
        <p className="text-[#4A5553]">{loc(game, "description", lang) || (lang === "ar" ? `لعبة ${cat(game.category, lang)} لمستوى ${lvl(game.level, lang)}` : `${cat(game.category, lang)} game for ${lvl(game.level, lang)} level`)}</p>
        <div className="flex gap-2 justify-center mt-3">
          <span className={`text-xs px-3 py-1 rounded-full ${LEVEL_COLOR[game.level]}`}>{lvl(game.level, lang)}</span>
          <span className="text-xs px-3 py-1 rounded-full bg-[#F0EFEB]">{cat(game.category, lang)}</span>
        </div>
      </div>
      <div className="mt-6">
        <label className="block text-sm mb-2">{lang === "ar" ? "اربط النتيجة بطفل (اختياري)" : "Link result to a child (optional)"}</label>
        <select data-testid="select-child-play" value={childId} onChange={e=>setChildId(e.target.value)} className="w-full px-4 py-2.5 rounded-lg border border-[#E5E2DB] bg-white">
          <option value="">{lang === "ar" ? "بدون ربط" : "No link"}</option>{children.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div className="text-center mt-6"><Btn data-testid="start-game-btn" onClick={start}><Play size={16} className="inline ms-1"/>{lang === "ar" ? "ابدأ اللعب" : "Start playing"}</Btn></div>
    </Card>
  </div>;

  const Engine = ENGINES[game.engine];
  return <div className="max-w-3xl mx-auto p-6 fade-up" data-testid="game-playing"><Card><Engine game={game} onComplete={finish}/></Card></div>;
}

// ============ Games Library ============
export function GamesLibrary() {
  const { lang } = useT();
  const [games, setGames] = useState([]);
  const [filter, setFilter] = useState({ level:"", category:"", engine:"", played:"" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const q = new URLSearchParams();
    if (filter.level) q.set("level", filter.level);
    if (filter.category) q.set("category", filter.category);
    if (filter.engine) q.set("engine", filter.engine);
    const { data } = await axios.get(`${API}/games?${q.toString()}`);
    setGames(data); setLoading(false);
  };
  useEffect(() => { load(); }, [filter.level, filter.category, filter.engine]); // eslint-disable-line

  let visible = games;
  if (filter.played === "played") visible = games.filter(g => g.played);
  if (filter.played === "unplayed") visible = games.filter(g => !g.played);
  if (filter.played === "favorites") visible = games.filter(g => g.favorite);

  const ENGINE_LABELS = lang === "ar"
    ? { multiple_choice: "اختيار", matching: "مطابقة", sequencing: "ترتيب", memory: "ذاكرة", sorting: "تصنيف" }
    : { multiple_choice: "Multiple choice", matching: "Matching", sequencing: "Sequencing", memory: "Memory", sorting: "Sorting" };

  return (
    <div className="space-y-6 fade-up" data-testid="games-library">
      <div><h1 className="text-4xl font-bold font-heading">{lang === "ar" ? "مكتبة ألعاب الدمج التفاعلية" : "Inclusive games library"}</h1>
        <p className="text-[#4A5553] mt-1">{lang === "ar" ? "24 لعبة هادئة وآمنة بصرياً مصممة خصيصاً لأطفال ذوي الاحتياجات الخاصة" : "24 calm, visually-safe games designed for learners with diverse support needs"}</p></div>

      <Card>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <select data-testid="filter-level" value={filter.level} onChange={e=>setFilter({...filter,level:e.target.value})} className="px-3 py-2 rounded-lg border border-[#E5E2DB] bg-white text-sm">
            <option value="">{lang === "ar" ? "كل المستويات" : "All levels"}</option>
            <option value="1">{lvl(1, lang)}</option><option value="2">{lvl(2, lang)}</option><option value="3">{lvl(3, lang)}</option></select>
          <select data-testid="filter-category" value={filter.category} onChange={e=>setFilter({...filter,category:e.target.value})} className="px-3 py-2 rounded-lg border border-[#E5E2DB] bg-white text-sm">
            <option value="">{lang === "ar" ? "كل الفئات" : "All categories"}</option>
            {Object.entries(CAT_LABEL).map(([k,v])=><option key={k} value={k}>{v[lang] || v.ar}</option>)}</select>
          <select data-testid="filter-engine" value={filter.engine} onChange={e=>setFilter({...filter,engine:e.target.value})} className="px-3 py-2 rounded-lg border border-[#E5E2DB] bg-white text-sm">
            <option value="">{lang === "ar" ? "كل الأنواع" : "All types"}</option>
            {Object.entries(ENGINE_LABELS).map(([k,v])=><option key={k} value={k}>{v}</option>)}</select>
          <select data-testid="filter-played" value={filter.played} onChange={e=>setFilter({...filter,played:e.target.value})} className="px-3 py-2 rounded-lg border border-[#E5E2DB] bg-white text-sm">
            <option value="">{lang === "ar" ? "الكل" : "All"}</option>
            <option value="played">{lang === "ar" ? "مُلعبت" : "Played"}</option>
            <option value="unplayed">{lang === "ar" ? "لم تُلعب" : "Unplayed"}</option>
            <option value="favorites">{lang === "ar" ? "المفضلة" : "Favorites"}</option></select>
        </div>
      </Card>

      {loading ? <p className="text-center text-[#4A5553]">{lang === "ar" ? "جاري التحميل..." : "Loading..."}</p> :
       visible.length === 0 ? <Card><p className="text-center text-[#4A5553] py-6">{lang === "ar" ? "لا توجد ألعاب مطابقة" : "No matching games"}</p></Card> :
       <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">{visible.map(g =>
        <Link key={g.code} to={`/games/play/${g.code}`} data-testid={`game-${g.code}`}>
          <Card className="hover:border-[#2F5233] hover:shadow-md cursor-pointer transition h-full">
            <div className="flex justify-between items-start mb-2">
              <span className="text-4xl">{g.icon}</span>
              <div className="flex flex-col items-end gap-1">
                <span className={`text-xs px-2 py-0.5 rounded-full ${LEVEL_COLOR[g.level]}`}>{lvl(g.level, lang)}</span>
                {g.played && <span className="text-xs text-[#3A7D44] flex items-center gap-1"><Check size={12}/>{lang === "ar" ? "أعلى:" : "Best:"} {g.best_score}%</span>}
              </div>
            </div>
            <h3 className="font-semibold text-sm">{loc(g, "title", lang)}</h3>
            <p className="text-xs text-[#4A5553] mt-1">{cat(g.category, lang)}</p>
          </Card>
        </Link>)}</div>}
    </div>
  );
}

// ============ Child Games Progress ============
export function ChildGamesProgress({ childId, badges: allBadges }) {
  const { lang } = useT();
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/games/progress/${childId}`).then(r => setData(r.data));
  }, [childId]);
  if (!data) return null;
  if (data.games_played === 0) return <Card><p className="text-sm text-[#4A5553] text-center py-3">{lang === "ar" ? "لم يلعب هذا الطفل أي لعبة بعد." : "This child hasn't played any game yet."} <Link to="/games" className="text-[#2F5233] underline">{lang === "ar" ? "استعرض المكتبة" : "Browse library"}</Link></p></Card>;
  const earned = data.badges || [];
  return <Card data-testid="child-games-progress">
    <h2 className="text-xl font-semibold mb-4 font-heading flex items-center gap-2"><Trophy size={20} className="text-[#E8A317]"/>{lang === "ar" ? "تقدم الألعاب" : "Game progress"}</h2>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
      <div className="text-center p-3 bg-[#F9F8F6] rounded-lg"><p className="text-2xl font-bold text-[#2F5233]">{data.games_played}</p><p className="text-xs text-[#4A5553]">{lang === "ar" ? "ألعاب مكتملة" : "Games completed"}</p></div>
      <div className="text-center p-3 bg-[#F9F8F6] rounded-lg"><p className="text-2xl font-bold text-[#769FCD]">{Object.keys(data.by_category).length}</p><p className="text-xs text-[#4A5553]">{lang === "ar" ? "فئات مختلفة" : "Different categories"}</p></div>
      <div className="text-center p-3 bg-[#F9F8F6] rounded-lg"><p className="text-sm font-bold text-[#3A7D44]">{data.strongest_category ? cat(data.strongest_category, lang) : "—"}</p><p className="text-xs text-[#4A5553]">{lang === "ar" ? "الأقوى" : "Strongest"}</p></div>
      <div className="text-center p-3 bg-[#F9F8F6] rounded-lg"><p className="text-sm font-bold text-[#E8A317]">{data.weakest_category ? cat(data.weakest_category, lang) : "—"}</p><p className="text-xs text-[#4A5553]">{lang === "ar" ? "يحتاج دعم" : "Needs support"}</p></div>
    </div>
    {allBadges?.length > 0 && <div>
      <p className="font-semibold mb-2">{lang === "ar" ? "الأوسمة" : "Badges"} ({earned.length}/{allBadges.length})</p>
      <div className="flex flex-wrap gap-2">{allBadges.map(b =>
        <div key={b.code} className={`px-3 py-2 rounded-lg flex items-center gap-2 border ${earned.includes(b.code)?"bg-[#E8A317]/10 border-[#E8A317]/30":"bg-[#F0EFEB] border-[#E5E2DB] opacity-50"}`} data-testid={`badge-${b.code}`}>
          {earned.includes(b.code) ? <span className="text-2xl">{b.icon}</span> : <Lock size={20} className="text-[#4A5553]"/>}
          <span className="text-sm font-medium">{loc(b, "label", lang)}</span></div>)}</div>
    </div>}
    {data.recent_sessions?.length > 0 && <div className="mt-4">
      <p className="font-semibold mb-2">{lang === "ar" ? "الجلسات الأخيرة" : "Recent sessions"}</p>
      <div className="space-y-1">{data.recent_sessions.slice(0,5).map(s =>
        <div key={s.id} className="flex justify-between items-center text-sm p-2 bg-[#F9F8F6] rounded">
          <span>{s.game_code}</span>
          <div className="flex items-center gap-2"><span className="text-xs text-[#4A5553]">{fmtDate(s.completed_at, lang)}</span>
            <span className="text-xs font-bold text-[#2F5233] bg-white px-2 py-0.5 rounded">{s.score}%</span></div>
        </div>)}</div>
    </div>}
  </Card>;
}
